import { PreviewIcon, ResponsiveButton } from "@datagame/ui";
import { useState } from "react";
import classNames from "classnames/bind";

import { FeedbackScreen } from "../FeedbackScreen";
import { Game, GameKind, SwiperConfiguration, WordopolisConfiguration } from "../../gql/graphql";
import { GameResultsPage } from "../GameResultsPage";
import { integrationApi } from "../../main";
import { isCardBasedGame } from "../../graphql/helpers/CardBasedGame";
import { isQuickPickGame } from "../../graphql/helpers/isQuickPickGame";
import { LeaderboardView } from "../LeaderboardView";
import { QuickRankGame } from "../QuickRankGame";
import { RankifierGame } from "../RankifierGame";
import { SwiperGame } from "../SwiperGame";
import { Transition } from "../shared/Transition";
import { TriviaGame } from "../TriviaGame";
import { useGlobalConfiguration } from "../../hooks/useGlobalConfiugration";
import { WelcomeScreen } from "../WelcomeScreen";
import { WordopolisGame } from "../WordopolisGame";
import { QuizGame } from "../QuizGame/QuizGame";

import styles from "./GameView.module.scss";
import { WordleGame } from "../WordleGame/WordleGame";

const cx = classNames.bind(styles);

export interface GameProps {
  game: Game;
  finishGame: (gameId: string) => void;
}

export enum GameSteps {
  WELCOME_PAGE = "Welcome Page",
  GAME = "GAME",
  GAME_RESULTS = "Game Results",
  FEEDBACK_PAGE = "Feedback Page",
  LEADERBOARD = "Leaderboard",
}

export const GameView = ({ game, finishGame }: GameProps) => {
  const [gameStep, setGameStep] = useState(
    game.configuration.showWelcomePage ? GameSteps.WELCOME_PAGE : GameSteps.GAME,
  );

  const [responseId, setResponseId] = useState("");

  const { isPreviewMode, hideDatagameBranding, showLeaderboard: showProjectLeaderboard } = useGlobalConfiguration();

  const gameTypes = {
    [GameKind.Swiper]: SwiperGame,
    [GameKind.QuickRank]: QuickRankGame,
    [GameKind.Rankifier]: RankifierGame,
    [GameKind.Wordopolis]: WordopolisGame,
    [GameKind.Trivia]: TriviaGame,
    [GameKind.Wordle]: WordleGame,
    [GameKind.QuizGame]: QuizGame,
  };

  const GameType = gameTypes[game.kind];

  const handleFinishGame = (newResponseId?: string) => {
    integrationApi.saveResponse(game.id, newResponseId ?? "");

    if (
      (game.configuration as SwiperConfiguration | WordopolisConfiguration).showLeaderboard &&
      !showProjectLeaderboard
    ) {
      setResponseId(newResponseId ?? "");
      return setGameStep(GameSteps.LEADERBOARD);
    }
    if (isCardBasedGame(game) && game.configuration.showGameResults) {
      setResponseId(newResponseId ?? "");
      return setGameStep(GameSteps.GAME_RESULTS);
    }
    if (game.configuration.showFeedbackPage) {
      return setGameStep(GameSteps.FEEDBACK_PAGE);
    }
    finishGame(game.id);
  };

  const handleFeedbackSave = () => {
    finishGame(game.id);
  };

  const handleGameResultsConfirm = () => {
    if (game.configuration.showFeedbackPage) {
      return setGameStep(GameSteps.FEEDBACK_PAGE);
    }
    finishGame(game.id);
  };

  const displayGameStep = () => {
    if (gameStep === GameSteps.WELCOME_PAGE) {
      return (
        <WelcomeScreen
          hideDatagameBranding={hideDatagameBranding}
          config={game.configuration.welcomePage}
          gameKind={game.kind}
          isQuickPickGame={isQuickPickGame(game)}
          theme={game.configuration.gameVisuals.themeColor}
          onClick={() => setGameStep(GameSteps.GAME)}
        />
      );
    }

    if (gameStep === GameSteps.GAME) {
      return (
        <Transition>
          <>
            <GameType game={game} finishGame={handleFinishGame} key={game.id} />
          </>
        </Transition>
      );
    }

    if (gameStep === GameSteps.FEEDBACK_PAGE && "feedbackPage" in game.configuration) {
      return (
        <FeedbackScreen
          config={game.configuration.feedbackPage}
          gameId={game.id}
          onSubmit={handleFeedbackSave}
          theme={game.configuration.gameVisuals.themeColor}
        />
      );
    }

    if (gameStep === GameSteps.LEADERBOARD) {
      return (
        <LeaderboardView
          gameId={game.id}
          responseId={responseId}
          onContinue={() => {
            if (isCardBasedGame(game) && game.configuration.showGameResults) {
              return setGameStep(GameSteps.GAME_RESULTS);
            }
            if (game.configuration.showFeedbackPage) {
              return setGameStep(GameSteps.FEEDBACK_PAGE);
            }
            finishGame(game.id);
          }}
        />
      );
    }

    if (gameStep === GameSteps.GAME_RESULTS && isCardBasedGame(game)) {
      return <GameResultsPage game={game} responseId={responseId} onConfirm={handleGameResultsConfirm} />;
    }

    return null;
  };

  return (
    <div className={styles.container}>
      {isPreviewMode && (
        <ResponsiveButton className={cx("previewBadge", game.kind)} color="red" iconSide="left" icon={<PreviewIcon />}>
          Preview
        </ResponsiveButton>
      )}
      {displayGameStep()}
    </div>
  );
};
